import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { FaPlus, FaFilter, FaEdit, FaTrash, FaEye, FaSearch, FaClipboardList, FaPills } from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import FilterModel from "./FilterModel";

const ObatPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [obatList, setObatList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    per_page: 10,
    page: 1,
    order_by: "name",
    status: "all",
    search: "",
  });
  const [totalPages, setTotalPages] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("role");
  const id_outlet = localStorage.getItem("id_outlet");

  const fetchObat = async () => {
    if (!token) return;
    const params = new URLSearchParams({
      per_page: appliedFilters.per_page,
      page: appliedFilters.page,
      order_by: appliedFilters.order_by,
      search: appliedFilters.search,
    });

    let url = `${process.env.REACT_APP_API_BASE_URL}/v1/drug`;
    if (userRole === "manager") {
      url += `?id_outlet=${id_outlet}&${params.toString()}`;
    } else {
      url += `?${params.toString()}`;
    }

    setLoading(true);
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Gagal mengambil data obat");

      setObatList(result.data || []);
      const { current_page, last_page, total } = result.meta || {};
      setCurrentPage(current_page || 1);
      setTotalPages(last_page || 1);
      setTotalData(total || 0);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Gagal mengambil data obat", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchObat();
  }, [appliedFilters]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setAppliedFilters((prev) => ({ ...prev, page: newPage }));
      setCurrentPage(newPage);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/drug/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Gagal mengimpor data obat");

      setShowSuccessModal(true);
      fetchObat();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Terjadi kesalahan saat mengimpor data obat.", "error");
    }
  };

  const handleSearchChange = (e) => {
    setAppliedFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters);
    setIsFilterOpen(false);
  };

  const handleDelete = (id, name) => {
    Swal.fire({
      title: `Apakah yakin menghapus ${name}?`,
      text: "Data obat akan dihapus permanen!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Ya, hapus!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/v1/drug/delete/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          Swal.fire("Terhapus!", "Data obat berhasil dihapus.", "success");
          fetchObat();
        } catch (err) {
          toast.error(err.response?.data?.message || "Gagal menghapus data");
        }
      }
    });
  };

  return (
    <main className="flex-1 min-h-0 flex flex-col px-6 pb-6 bg-green-50 gap-4">
      {/* PANEL */}
      <div className="bg-white rounded-3xl shadow-xl p-6 flex flex-col gap-4">
        {/* Panel Header */}
        <div className="flex items-center gap-3 mb-2">
          <FaPills className="text-green-600 text-3xl" />
          <h1 className="text-3xl font-bold text-gray-800">Obat</h1>
        </div>
        <p className="text-gray-600 mb-4">Kelola semua obat dengan mudah dan cepat</p>

        {/* Action Buttons */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="flex gap-2 flex-wrap">
            {userRole !== "manager" && (
              <Link to="/obat-paket/obat/create">
                <button className="flex items-center gap-2 bg-yellow-400 text-white px-4 py-2 rounded-lg shadow-md hover:bg-yellow-500 hover:scale-105 transition transform text-sm font-medium">
                  <FaPlus /> Buat Obat
                </button>
              </Link>
            )}
            <Link to="/obat-paket/obat/register">
              <button className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-green-600 hover:scale-105 transition transform text-sm font-medium">
                <FaClipboardList /> Register Obat
              </button>
            </Link>
            {userRole !== "manager" && (
              <label className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-blue-600 hover:scale-105 transition transform text-sm font-medium cursor-pointer">
                <FaPlus /> Import Obat
                <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} className="hidden" />
              </label>
            )}
          </div>

          {/* Search & Filter */}
          <div className="flex justify-end gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Cari obat..."
                value={appliedFilters.search}
                onChange={handleSearchChange}
                className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring focus:ring-green-200 text-sm"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="bg-white border border-gray-300 px-4 py-2 rounded-xl shadow hover:bg-gray-100 flex items-center gap-2 text-sm font-medium transform hover:scale-105 transition"
            >
              <FaFilter className="text-blue-500" /> Filter
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-y-auto bg-white rounded-2xl border border-gray-200 shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="sticky top-0 z-10 bg-green-100">
              <tr>
                <th className="px-4 py-3">No</th>
                <th className="px-4 py-3">Nama</th>
                <th className="px-4 py-3">Produsen</th>
                <th className="px-4 py-3">Dosis</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Harga Beli</th>
                <th className="px-4 py-3">Harga Jual</th>
                <th className="px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-5 py-4 text-center text-gray-500 italic">
                    ⏳ Memuat data...
                  </td>
                </tr>
              ) : obatList.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-5 py-4 text-center text-gray-500 italic">
                    Tidak ada data ditemukan.
                  </td>
                </tr>
              ) : (
                obatList.map((item, index) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50 text-gray-800 transition">
                    <td className="px-4 py-3">{index + 1}</td>
                    <td className="px-4 py-3 font-medium">{item.name}</td>
                    <td className="px-4 py-3">{item.produsen}</td>
                    <td className="px-4 py-3">{item.dosis}</td>
                    <td className="px-4 py-3">{item.stock}</td>
                    <td className="px-4 py-3">
                      Rp {item.harga_beli.toLocaleString("id-ID", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3">
                      Rp {item.harga_jual.toLocaleString("id-ID", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 flex gap-2">
                      <Link
                        to={`/obat-paket/obat/detail/${encodeURI(item.name + "_" + item.dosis + "_" + item.produsen)}`}
                        className="p-2 bg-green-100 hover:bg-green-200 rounded-lg shadow-md text-green-600 hover:text-green-700 transition transform hover:scale-110"
                      >
                        <FaEye />
                      </Link>
                      {userRole === "owner" && (
                        <>
                          <Link
                            to={`/obat-paket/obat/edit/${encodeURI(item.name + "_" + item.dosis + "_" + item.produsen)}`}
                            className="p-2 bg-yellow-100 hover:bg-yellow-200 rounded-lg shadow-md text-yellow-600 hover:text-yellow-700 transition transform hover:scale-110"
                          >
                            <FaEdit />
                          </Link>
                          <button
                            onClick={() => handleDelete(item.id, item.name)}
                            className="p-2 bg-red-100 hover:bg-red-200 rounded-lg shadow-md text-red-600 hover:text-red-700 transition transform hover:scale-110"
                          >
                            <FaTrash />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-3">
          <p className="text-sm text-gray-500">
            Menampilkan {obatList.length} dari total {totalData} obat
          </p>
          <div className="flex items-center space-x-2">
            <button
              className="flex items-center px-3 py-1 text-sm bg-green-100 hover:bg-gray-200 text-gray-700 rounded-xl shadow-md transform hover:scale-105 transition"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <FiChevronLeft /> Sebelumnya
            </button>
            <span className="text-sm text-gray-600">
              Halaman <strong>{Math.min(currentPage, totalPages)}</strong> dari <strong>{totalPages}</strong>
            </span>
            <button
              className="flex items-center px-3 py-1 text-sm bg-green-100 hover:bg-gray-200 text-gray-700 rounded-xl shadow-md transform hover:scale-105 transition"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Berikutnya <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-sm">
            <h2 className="text-xl font-semibold text-green-600 mb-4">Import Berhasil!</h2>
            <p className="text-gray-700 mb-6">Data obat berhasil diimpor.</p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition"
            >
              Tutup
            </button>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      <FilterModel isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} onApply={handleApplyFilters} />

      <Toaster position="top-right" reverseOrder={true} />
    </main>
  );
};

export default ObatPage;
